import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import TestimonialCard from '../ui/testimonial-card';
import { supabase } from '../../lib/supabase';

interface TestimonialsSectionProps {
  title?: string;
  subtitle?: string;
}

export default function TestimonialsSection({
  title = "Ils nous font confiance ♥️",
  subtitle = "Des entreprises convaincues par notre méthode RH",
}: TestimonialsSectionProps) {
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      const { data } = await supabase
        .from('testimonials')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(6);

      setTestimonials(data || []);
      setIsLoading(false);
    };

    fetchTestimonials();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.165, 0.84, 0.44, 1] },
    },
  };

  if (isLoading) {
    return (
      <section className="py-20 overflow-visible">
        <div className="container-custom overflow-visible">
          <div className="max-w-3xl mx-auto text-center mb-16 pb-4">
            <h2 className="section-title">{title}</h2>
            {subtitle && <p className="text-xl text-neutral-600">{subtitle}</p>}
          </div>
          <div className="flex justify-center">
            <div className="animate-pulse w-32 h-32 bg-neutral-200 rounded-full" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 overflow-visible">
      <div className="container-custom overflow-visible">
        <div className="max-w-3xl mx-auto text-center mb-16 pb-4">
          <h2 className="section-title">{title}</h2>
          {subtitle && <p className="text-xl text-neutral-600">{subtitle}</p>}
        </div>

        <div className="relative overflow-visible pt-8">
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 z-[1]"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
          >
            {testimonials.map((testimonial) => (
              <motion.div key={testimonial.id} variants={itemVariants}>
                <TestimonialCard {...testimonial} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
